import { type ApplyRequest, isInFlight } from "@m8t-stack/platform-release";

/**
 * The out-of-band door for a wedged apply-request.
 *
 * Every ordinary route out of a stuck row runs INSIDE the updater job: the lease
 * reaper, the takeover budget, the terminal writes. That is fine until the thing
 * that is broken is the job itself — and it has been. A canary install sat
 * `applying` for twelve days with an expired lease and a healthy-looking updater
 * ticking every five minutes: the running engine predated its own lease-rescue
 * fix, so it could not claim the row, and the self-update that would have
 * replaced it ran only AFTER a successful claim. The repair existed and could
 * not reach the machine that needed it.
 *
 * `BLOCKS_NEW_INTENT` then seals it — no fresh intent can be written past a
 * non-terminal row — so the install cannot be updated by any means at all.
 *
 * This is the door that does not depend on the engine being able to run.
 */

export type ClearDecision =
  | { action: "none"; reason: string }
  | { action: "clear"; reason: string }
  | { action: "refuse"; reason: string };

/** Why the row was cleared, recorded on the row itself so the next reader is not
 *  left guessing whether an update genuinely failed or an operator intervened. */
export const CLEARED_BY_OPERATOR = "cleared by an operator via `m8t platform clear-intent`";

/**
 * Pure. Decide what clearing this row should do.
 *
 * The one judgement call: a row that is in-flight with a LIVE lease is a
 * converge that is genuinely running right now. Clearing it would let a second
 * apply start alongside the first, both mutating the same infrastructure. That
 * is refused unless the operator forces it, because "the update looks stuck" and
 * "the update is still working" are indistinguishable from the outside, and only
 * one of them is safe to interrupt.
 */
export function decideClear(row: ApplyRequest | null, nowIso: string, force: boolean): ClearDecision {
  if (!row) return { action: "none", reason: "there is no apply-request to clear." };

  if (!isInFlight(row.status) && row.status !== "pending" && row.status !== "awaiting-engine-update") {
    return { action: "none", reason: `the apply-request is already terminal (${row.status}) — a new intent can be written.` };
  }

  const leaseLive =
    isInFlight(row.status) && row.leaseUntil !== null && Date.parse(row.leaseUntil) > Date.parse(nowIso);

  if (leaseLive && !force) {
    return {
      action: "refuse",
      reason:
        `the apply-request is ${row.status} and its lease is still live until ${row.leaseUntil ?? "(unknown)"} — ` +
        `a converge may be running right now. Clearing it could start a second apply alongside the first. ` +
        `Wait for the lease to expire, or pass --force if you know the engine is gone.`,
    };
  }

  const held = leaseLive ? " (lease still live — forced)" : "";
  return { action: "clear", reason: `clearing a ${row.status} apply-request for target ${row.target}${held}.` };
}

/** Pure. The terminal row a clear writes. Deliberately `failed` rather than a
 *  new status: every existing reader already treats `failed` as terminal and
 *  overwritable, so this needs no new case anywhere — and the error text says
 *  plainly that this was an intervention, not an update that went wrong. */
export function clearedRow(row: ApplyRequest, nowIso: string): ApplyRequest {
  return {
    ...row,
    status: "failed",
    result: {
      appliedVersion: row.result?.appliedVersion ?? null,
      error: `${CLEARED_BY_OPERATOR} (was ${row.status}${row.phase ? `, phase "${row.phase}"` : ""})`,
    },
    updatedAt: nowIso,
  };
}
